import { Header as DatagridHeader } from "@dashboard/components/Datagrid/components/Header";
import {
  numberCell,
  readonlyTextCell,
  textCell,
} from "@dashboard/components/Datagrid/customCells/cells";
import Datagrid from "@dashboard/components/Datagrid/Datagrid";
import {
  DatagridChangeOpts,
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { AvailableColumn } from "@dashboard/components/Datagrid/types";
import { WsmErrorFragment } from "@dashboard/graphql";
import { GridCell, Item } from "@glideapps/glide-data-grid";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { getWsmDealerErrorMessage } from "../../errors";
import { tierMessages } from "../../messages";
import { WsmTierRowDraft } from "../../types";
import { formatTierAmountDisplay } from "../../utils";
import {
  TIER_COLUMN_ORDER,
  TIER_FIXED_COLUMN_WIDTHS,
  TierColumnId,
  tierProductWidth,
} from "./columns";

export interface WsmDealerTierPricesCardProps {
  rows: WsmTierRowDraft[];
  /** One error per row index, as the bulk payload named them. */
  rowErrors: Record<number, WsmErrorFragment>;
  currencyCode: string | null;
  disabled: boolean;
  /** True when the group holds more prices than one screen loaded. */
  truncatedAt: number | null;
  unsavedCount: number;
  onAdd: () => void;
  onPaste: () => void;
  onRemove: (keys: string[]) => void;
  onRowChange: (key: string, patch: Partial<WsmTierRowDraft>) => void;
}

/**
 * The grid a dealer price list is actually edited in. Datagrid brings its own
 * DashboardCard and its own virtualiser, which is why 300 rows paint in one
 * frame and why this card must NOT be wrapped in a second card.
 */
export const WsmDealerTierPricesCard = ({
  rows,
  rowErrors,
  currencyCode,
  disabled,
  truncatedAt,
  unsavedCount,
  onAdd,
  onPaste,
  onRemove,
  onRowChange,
}: WsmDealerTierPricesCardProps) => {
  const intl = useIntl();
  const datagridState = useDatagridChangeState();
  const wrapper = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(0);

  /**
   * The grid sizes itself to the well it is in, because that well is 495px at a
   * 1024 viewport and 773px at 1440, and a fixed set of columns wide enough to
   * read at 1440 puts the price off screen at both.
   */
  useEffect(() => {
    const node = wrapper.current;

    if (!node || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(entries => {
      setGridWidth(entries[0].contentRect.width);
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const columns: AvailableColumn[] = useMemo(() => {
    const titles: Record<TierColumnId, string> = {
      product: intl.formatMessage(tierMessages.colProduct),
      sku: intl.formatMessage(tierMessages.colSku),
      minQuantity: intl.formatMessage(tierMessages.colMinQuantity),
      amount: currencyCode
        ? intl.formatMessage(tierMessages.colAmount, { currency: currencyCode })
        : intl.formatMessage(tierMessages.colAmountNoCurrency),
    };
    const widths: Record<TierColumnId, number> = {
      ...TIER_FIXED_COLUMN_WIDTHS,
      product: tierProductWidth(gridWidth),
    };

    return TIER_COLUMN_ORDER.map(id => ({ id, title: titles[id], width: widths[id] }));
  }, [intl, currencyCode, gridWidth]);
  const getCellContent = useCallback(
    ([column, row]: Item): GridCell => {
      const tier = rows[row];
      const columnId = columns[column]?.id;

      if (!tier || !columnId) {
        return readonlyTextCell("");
      }

      switch (columnId) {
        case "product":
          return readonlyTextCell(tier.productName);
        case "sku":
          return readonlyTextCell(tier.sku || tier.variantName);
        case "minQuantity":
          return numberCell(tier.minQuantity);
        case "amount":
          // A text cell, not a money cell: the merchant's keystrokes go to the
          // server verbatim so TIER_AMOUNT_TOO_MANY_DECIMALS is a refusal they
          // can see rather than a rounding they cannot. The cell's data is what
          // the editor opens on and what the save path sends; only the drawn
          // text is money-shaped, which is why a stored 270.000 reads as
          // currency and still round-trips as 270.000.
          return textCell(tier.amount, {
            contentAlign: "right",
            displayData: formatTierAmountDisplay(tier.amount, currencyCode),
          });
        default:
          return readonlyTextCell("");
      }
    },
    [rows, columns, currencyCode],
  );
  const handleChange = useCallback(
    (opts: DatagridChangeOpts) => {
      const update = opts.currentUpdate;
      const tier = update ? rows[update.row] : undefined;

      if (update && tier) {
        if (update.column === "minQuantity") {
          onRowChange(tier.key, { minQuantity: Math.max(1, Number(update.data) || 1) });
        }

        if (update.column === "amount") {
          onRowChange(tier.key, { amount: String(update.data ?? "").trim() });
        }
      }

      // The form state is the single source of truth for the grid, so the
      // grid's own change buffer is emptied rather than reconciled at save.
      datagridState.clear();
    },
    [rows, onRowChange, datagridState],
  );
  const firstError = Object.keys(rowErrors).length
    ? rowErrors[Number(Object.keys(rowErrors)[0])]
    : undefined;
  const errorText = getWsmDealerErrorMessage(firstError, intl);

  return (
    <Box ref={wrapper} data-test-id="wsm-dealer-tier-prices">
      <DatagridChangeStateContext.Provider value={datagridState}>
        <Datagrid
          availableColumns={columns}
          rows={rows.length}
          emptyText={intl.formatMessage(tierMessages.empty)}
          getCellContent={getCellContent}
          getCellError={([, row]: Item) => !!rowErrors[row]}
          menuItems={index => [
            {
              label: intl.formatMessage(tierMessages.removeRow),
              onSelect: () => onRemove([rows[index]?.key]),
            },
          ]}
          selectionActions={(selection, { removeRows }) => (
            <Button
              variant="tertiary"
              disabled={disabled}
              onClick={() => {
                onRemove(selection.map(index => rows[index]?.key).filter(Boolean));
                removeRows(selection);
              }}
              data-test-id="wsm-dealer-tier-remove-selected"
            >
              {intl.formatMessage(tierMessages.removeSelected)}
            </Button>
          )}
          onChange={handleChange}
          readonly={disabled}
          rowMarkers="checkbox"
          verticalBorder={false}
          renderHeader={() => (
            <DatagridHeader title={intl.formatMessage(tierMessages.title, { count: rows.length })}>
              {unsavedCount > 0 && (
                <Text size={2} color="default2" data-test-id="wsm-dealer-tier-unsaved">
                  {intl.formatMessage(tierMessages.unsaved, { count: unsavedCount })}
                </Text>
              )}
              <Button
                variant="secondary"
                disabled={disabled}
                onClick={onPaste}
                data-test-id="wsm-dealer-tier-paste"
              >
                {intl.formatMessage(tierMessages.pastePrices)}
              </Button>
              <Button
                variant="secondary"
                disabled={disabled}
                onClick={onAdd}
                data-test-id="wsm-dealer-tier-add"
              >
                {intl.formatMessage(tierMessages.addPrice)}
              </Button>
            </DatagridHeader>
          )}
        />
      </DatagridChangeStateContext.Provider>
      {(errorText || truncatedAt) && (
        <Box paddingX={6} paddingTop={2} display="grid" gap={1}>
          {errorText && (
            <Text size={2} color="critical1" data-test-id="wsm-dealer-tier-error">
              {errorText}
            </Text>
          )}
          {truncatedAt && (
            <Text size={2} color="default2" data-test-id="wsm-dealer-tier-truncated">
              {intl.formatMessage(tierMessages.truncated, { count: truncatedAt })}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WsmDealerTierPricesCard;
